import React, { type ReactNode } from 'react';
import { type MapDispatchToProps, type MapStateToProps, connect } from 'react-redux';
import { Link } from 'react-router';
import { compose } from 'redux';

import { type IDetermineShouldDropSearchToNextLineProps } from 'src/components/SearchResponsiveHelper/SearchResponsiveHelper';

import { showedBackstagePage } from '../../actions';
import * as D from '../../deps';
import { isBackstageRightRailActive } from '../../utils';
import BackstageHeroSidebarImage from '../BackstageHeroSidebarImage';
import BackstageLongPlayButton from '../BackstageLongPlayButton';

import BackstageLargeHeaderStickySidebar from './BackstageLargeHeaderStickySidebar';

import './BackstageLargeHeaderLayout.scss';

const HeroImageWithLogger = D.withVisibilityLogger({
  contentSection: D.Constants.ContentSections.BackstageContentSections.HERO_IMAGE,
})(D.Img);

interface IBackstageLargeHeaderLayoutStateProps {
  currentPathname: ReturnType<typeof D.getCurrentPathnameFromState>;
  dropToNextLine: boolean;
  isCuratorExperienceEnabled: ReturnType<typeof D.Utils.TunerVars.curatorExperienceEnabled>;
  isRightRailActive: ReturnType<typeof isBackstageRightRailActive>;
  mobileLongLink: ReturnType<typeof D.getMobileLongLink>;
}

interface IBackstageLargeHeaderLayoutDispatchProps {
  showedBackstagePage: typeof showedBackstagePage;
}

interface IBackstageLargeHeaderLayoutOwnProps {
  artistBio: ReactNode;
  // The `entityGetterFunc` will be called with `entityIdentifier` to retrieve data to render.
  // Original backstage pages used an seoToken parsed from the URL. Newer pages use the pandoraId associated with an entity
  entityGetterFunc: $TSFixMeFunction;
  entityIdentifier: string;
  headerBackstage: ReactNode;
  heroImageIsCircle: boolean;
  heroImageLinkUrl: string;
  heroImageUrl: string;
  isCuratorExperienceEnabled: $TSFixMeFunction;
  isRightRailActive: $TSFixMeFunction;
  largeHeader: ReactNode;
  mobileLongLink: string;
  playOnClick: $TSFixMeFunction;
  renderArtworkPlayButton: boolean;
  renderMainContent: $TSFixMeFunction;
  renderSidebarButtons: $TSFixMeFunction;
  renderStructuredMetaData: $TSFixMeFunction;
}

type OwnPropsFromHoCs = IBackstageLargeHeaderLayoutOwnProps &
  IDetermineShouldDropSearchToNextLineProps;

type IBackstageLargeHeaderLayoutProps = IBackstageLargeHeaderLayoutStateProps &
  IBackstageLargeHeaderLayoutDispatchProps &
  IBackstageLargeHeaderLayoutOwnProps;

export class BackstageLargeHeaderLayout extends React.Component<IBackstageLargeHeaderLayoutProps> {
  componentDidMount(): void {
    this.fetchData();
    this.logViewed();
  }

  componentDidUpdate(prevProps: IBackstageLargeHeaderLayoutProps): void {
    if (
      D.isBackstageParentPath(this.props.currentPathname) &&
      this.props.entityIdentifier !== prevProps.entityIdentifier
    ) {
      D.Utils.Scroll.scrollToTop();
      this.fetchData();
    }
    if (
      (this.props.currentPathname !== prevProps.currentPathname &&
        D.isBackstageChildPath(this.props.currentPathname)) ||
      this.props.entityIdentifier !== prevProps.entityIdentifier
    ) {
      this.logViewed();
    }
  }

  fetchData = (): void => {
    const { entityIdentifier, entityGetterFunc } = this.props;
    if (entityIdentifier && entityGetterFunc) {
      entityGetterFunc(entityIdentifier);
    }
  };

  logViewed = (): void => {
    if (this.props.entityIdentifier) {
      this.props.showedBackstagePage({ pageId: this.props.entityIdentifier });
    }
  };

  render(): ReactNode {
    const {
      isRightRailActive,
      isCuratorExperienceEnabled,
      renderArtworkPlayButton,
      dropToNextLine,
      renderStructuredMetaData,
      mobileLongLink,
      largeHeader,
      headerBackstage,
      heroImageIsCircle,
      heroImageLinkUrl,
      heroImageUrl,
      playOnClick,
      renderSidebarButtons,
      currentPathname,
      artistBio,
      renderMainContent,
    } = this.props;

    const classes = D.createBEM('BackstageLargeHeaderLayout', {
      dropToNextLine,
      rightRail: isRightRailActive,
      hasHeaderLogo: renderArtworkPlayButton,
      fullBio: D.isBackstageArtistFullBioPath(currentPathname),
    });

    return (
      <div className={classes} data-qa={D.QA.BS}>
        {renderStructuredMetaData && renderStructuredMetaData()}
        {renderArtworkPlayButton ? (
          <a href={mobileLongLink} className='BackstageLargeHeaderLayout__logo'>
            <D.Icon className='BackstageLargeHeaderLayout__logo__icon' glyph={D.LogoPandoraIcon} />
          </a>
        ) : (
          largeHeader
        )}
        <div className='BackstageLargeHeaderLayout__body'>
          <header className='BackstageLargeHeaderLayout__body__header'>{headerBackstage}</header>
          <aside className='BackstageLargeHeaderLayout__body__aside'>
            <BackstageLargeHeaderStickySidebar>
              <BackstageHeroSidebarImage
                heroImageIsCircle={heroImageIsCircle}
                isCuratorExperienceEnabled={isCuratorExperienceEnabled}
                isRightRailActive={isRightRailActive}
                renderArtworkPlayButton={renderArtworkPlayButton}
              >
                <Link to={heroImageLinkUrl}>
                  <HeroImageWithLogger
                    // @ts-expect-error TS(2322) FIXME: Type '{ src: any; isCircle: any; useDefaultBgColor... Remove this comment to see the full error message
                    src={heroImageUrl}
                    isCircle={heroImageIsCircle}
                    useDefaultBgColor={true}
                  />
                  {renderArtworkPlayButton && (
                    <BackstageLongPlayButton
                      playOnClick={playOnClick}
                      isArtist={heroImageIsCircle}
                    />
                  )}
                </Link>
              </BackstageHeroSidebarImage>
              {renderSidebarButtons?.()}
              {!D.isBackstageArtistFullBioPath(currentPathname) && artistBio}
            </BackstageLargeHeaderStickySidebar>
          </aside>
          <main className='BackstageLargeHeaderLayout__body__main'>
            {renderMainContent && renderMainContent()}
          </main>
        </div>
        <D.Footer useLightTheme={true} />
      </div>
    );
  }
}

export const mapStateToProps: MapStateToProps<
  IBackstageLargeHeaderLayoutStateProps,
  OwnPropsFromHoCs,
  $TSFixMe
> = (state: $TSFixMe, ownProps: OwnPropsFromHoCs) => ({
  currentPathname: D.getCurrentPathnameFromState(state),
  dropToNextLine: ownProps.shouldDropSearchToNextLine && !ownProps.renderArtworkPlayButton,
  isCuratorExperienceEnabled: D.Utils.TunerVars.curatorExperienceEnabled(state),
  isRightRailActive: isBackstageRightRailActive(state),
  mobileLongLink: D.getMobileLongLink(state, {
    pageUrl: D.getCurrentPathnameFromState(state),
    feature: D.Constants.Urls.deepLinkParams.BACKSTAGE_PAGE,
  }),
});

const mapDispatchToProps: MapDispatchToProps<
  IBackstageLargeHeaderLayoutDispatchProps,
  IBackstageLargeHeaderLayoutOwnProps
> = {
  showedBackstagePage,
};

const enhance = compose(
  D.BgSetter,
  D.determineShouldDropSearchToNextLine,
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(BackstageLargeHeaderLayout);
